import { Injectable } from '@angular/core';
import { debug } from '@4players/odin-common';
import { isTauri } from '../../shared/helpers/platform';

/**
 * Normalized stats of one video stream for the stream stats dialog. One
 * snapshot per second; rates are deltas between consecutive snapshots.
 * Pipeline facts (codec, encoder, decoder) only exist natively; the browser
 * report contributes its encoder/decoder implementation instead. Missing
 * values stay undefined and render as "–".
 */
export interface StreamStats {
  direction: 'send' | 'receive';
  codec?: string;
  encoder?: string;
  decoder?: string;
  width?: number;
  height?: number;
  fps?: number;
  bitrateKbps?: number;
  rttMs?: number;
  jitterMs?: number;
  lossPct?: number;
  nack?: number;
  pli?: number;
  keyframes?: number;
  keyframesPerSec?: number;
  /** Receive side, cumulative: frames dropped before display. */
  droppedFrames?: number;
  /** Receive side, cumulative: viewer-visible freezes. */
  freezes?: number;
  /** Send side, native only: current encoder cap and its ceiling. */
  bweCapKbps?: number;
  bweMaxKbps?: number;
  retransmittedKb?: number;
  /** Browser only: why the encoder limits quality (cpu/bandwidth). */
  qualityLimitation?: string;
  timestamp: number;
}

/** Identifies the stream a caller wants stats for. */
export interface StatsTarget {
  direction: 'send' | 'receive';
  /** Native build: the server-allocated media id of the slot. */
  mediaId?: number;
  /** Web build: the id of the rendered MediaStreamTrack. */
  trackId?: string;
  /** Last-resort source for frame dimensions (native send side). */
  stream?: MediaStream;
}

/**
 * Raw facts of one stream for the stream health state machine. Everything
 * but `announced`/`bound` is optional: browser and native report different
 * subsets, and a missing value means "unknown", never zero.
 */
export interface HealthSample {
  at: number;
  native: boolean;
  direction: 'send' | 'receive';
  /** The session knows the slot (native: media map entry; web: RTP entry). */
  announced: boolean;
  /** RTP arrived at the session level. */
  bound: boolean;
  packetsReceived?: number;
  packetsLost?: number;
  bytesReceived?: number;
  /** Assembled/decoded (receive) or encoded (send) frames, cumulative. */
  frames?: number;
  keyframes?: number;
  /** Keyframe requests we sent. */
  pliCount?: number;
  freezes?: number;
  dropped?: number;
  codec?: string;
  decoder?: string;
  width?: number;
  height?: number;
  packetsSent?: number;
  bytesSent?: number;
  encoder?: string;
  qualityLimitation?: string;
  /**
   * The server's receiver report on our outbound stream: its presence and
   * freshness tell whether the server sees us at all. `stamp` is an opaque
   * change token.
   */
  ack?: { stamp: string; lossPct?: number; rttMs?: number };
}

type StatsEntry = Record<string, unknown>;

/** Native pipeline facts per media id. */
interface PipelineFacts {
  codec?: string;
  encoder?: string;
  decoder?: string;
  frames?: number;
  width?: number;
  height?: number;
  keyframes?: number;
  dropped?: number;
  stalls?: number;
  bwe_cap_bps?: number;
  bwe_max_bps?: number;
}

/** Raw snapshot shared by every caller within its maximum age. */
interface RawSnapshot {
  at: number;
  /** Native reports use ms for round-trip times, browsers seconds. */
  native: boolean;
  entries: StatsEntry[];
  /** Native only: media id → { direction, mid, ssrc }. */
  media: Record<string, { direction: string; mid?: string; ssrc?: number }>;
  pipeline: Record<string, PipelineFacts>;
  /**
   * Native only: QUIC round-trip time of the voice connection to the same
   * server, the fallback where no RTP-level RTT exists.
   */
  transportRttMs?: number;
}

/**
 * Reads WebRTC stats of the video connection: the browser report of the
 * ODIN web plugin, or the native plugin's report plus pipeline registry
 * inside Tauri. Snapshots are deduplicated, so any number of tiles and
 * dialogs cause at most one stats poll per second.
 */
@Injectable({ providedIn: 'root' })
export class StreamStatsService {
  // Registered by OdinService on join (the web plugin keeps its room private).
  private webStatsSource: (() => Promise<RTCStatsReport | undefined>) | null = null;

  private lastSnapshot: RawSnapshot | null = null;
  private prevSnapshot: RawSnapshot | null = null;
  private snapshotInFlight: Promise<RawSnapshot | null> | null = null;
  private lastSnapshotErrorAt = 0;

  registerWebStatsSource(source: (() => Promise<RTCStatsReport | undefined>) | null): void {
    this.webStatsSource = source;
    this.lastSnapshot = null;
    this.prevSnapshot = null;
  }

  /** Normalized stats of one stream, or null while none are available. */
  async sample(target: StatsTarget): Promise<StreamStats | null> {
    const snap = await this.snapshot();
    if (!snap) return null;
    const entry = this.findRtpEntry(snap, target);
    if (!entry) return null;

    const p = target.mediaId !== undefined ? snap.pipeline[String(target.mediaId)] : undefined;
    const stats: StreamStats = { direction: target.direction, timestamp: snap.at };
    stats.codec = p?.codec ?? this.codecFromEntry(snap, entry);
    stats.encoder = p?.encoder ?? str(entry['encoderImplementation']);
    stats.decoder = p?.decoder ?? str(entry['decoderImplementation']);

    // The native report has no frame dimensions: the decoder records them in
    // the pipeline registry, the send side falls back to the track settings.
    stats.width = num(entry['frameWidth']) ?? p?.width;
    stats.height = num(entry['frameHeight']) ?? p?.height;
    if (stats.width === undefined || stats.height === undefined) {
      const settings = target.stream?.getVideoTracks()[0]?.getSettings();
      if (settings?.width && settings?.height) {
        stats.width = settings.width;
        stats.height = settings.height;
      }
    }
    stats.fps = num(entry['framesPerSecond']);
    stats.nack = num(entry['nackCount']);
    stats.pli = num(entry['pliCount']);
    stats.keyframes = num(entry['keyFramesEncoded'] ?? entry['keyFramesDecoded']) ?? p?.keyframes;
    if (target.direction === 'receive') {
      stats.droppedFrames = p?.dropped ?? num(entry['framesDropped']);
      stats.freezes = p?.stalls ?? num(entry['freezeCount']);
    } else if (p?.bwe_cap_bps !== undefined) {
      stats.bweCapKbps = Math.round(p.bwe_cap_bps / 1000);
      if (p.bwe_max_bps !== undefined) stats.bweMaxKbps = Math.round(p.bwe_max_bps / 1000);
    }
    stats.jitterMs = scale(num(entry['jitter']), 1000);
    stats.qualityLimitation = str(entry['qualityLimitationReason']);
    const retransmitted = num(entry['retransmittedBytesSent']);
    if (retransmitted !== undefined) stats.retransmittedKb = Math.round(retransmitted / 1024);

    this.addRates(stats, snap, entry, target);

    // Send side: RTT and remote-reported loss live on the remote-inbound
    // entry of our ssrc. Receive side: loss from the own inbound entry, RTT
    // from the remote-outbound entry (needs a DLRR block, often absent).
    if (target.direction === 'send') {
      const ri = snap.entries.find(
        (e) => typeName(e) === 'remote-inbound-rtp' && e['ssrc'] === entry['ssrc']
      );
      if (ri) {
        stats.rttMs = scale(num(ri['roundTripTime']), snap.native ? 1 : 1000);
        stats.lossPct = pct(num(ri['fractionLost']));
      }
    } else {
      const lost = num(entry['packetsLost']);
      const received = num(entry['packetsReceived']);
      if (lost !== undefined && received !== undefined && lost + received > 0) {
        stats.lossPct = Math.round((lost / (lost + received)) * 1000) / 10;
      }
      const ro = snap.entries.find(
        (e) => typeName(e) === 'remote-outbound-rtp' && e['ssrc'] === entry['ssrc']
      );
      if (ro) stats.rttMs = scale(num(ro['roundTripTime']), snap.native ? 1 : 1000);
    }
    // Connection-level fallbacks: every stream shares the transport.
    stats.rttMs ??=
      this.remoteInboundRtt(snap) ?? this.candidatePairRtt(snap) ?? snap.transportRttMs;
    return stats;
  }

  /**
   * Raw facts of one stream for the health state machine. Unlike `sample`
   * this also answers while no RTP entry exists yet: that gap is the
   * interesting signal. Null only while there is no stats source at all.
   */
  async healthSample(target: StatsTarget, maxAgeMs = 900): Promise<HealthSample | null> {
    const snap = await this.snapshot(maxAgeMs);
    if (!snap) return null;
    const entry = this.findRtpEntry(snap, target);
    const link = target.mediaId !== undefined ? snap.media[String(target.mediaId)] : undefined;
    const p = target.mediaId !== undefined ? snap.pipeline[String(target.mediaId)] : undefined;
    const send = target.direction === 'send';
    const sample: HealthSample = {
      at: snap.at,
      native: snap.native,
      direction: target.direction,
      announced: snap.native ? link !== undefined : entry !== null,
      bound: snap.native ? link?.ssrc !== undefined || send : entry !== null,
    };
    if (p) {
      sample.frames = p.frames;
      sample.keyframes = p.keyframes;
      sample.codec = p.codec;
      sample.width = p.width;
      sample.height = p.height;
      if (send) {
        sample.encoder = p.encoder;
      } else {
        sample.freezes = p.stalls;
        sample.dropped = p.dropped;
        sample.decoder = p.decoder;
      }
    }
    if (!entry) return sample;

    sample.codec ??= this.codecFromEntry(snap, entry);
    sample.width ??= num(entry['frameWidth']);
    sample.height ??= num(entry['frameHeight']);
    if (send) {
      sample.packetsSent = num(entry['packetsSent']);
      sample.bytesSent = num(entry['bytesSent']);
      sample.frames ??= num(entry['framesEncoded']);
      sample.keyframes ??= num(entry['keyFramesEncoded']);
      sample.encoder ??= str(entry['encoderImplementation']);
      sample.qualityLimitation = str(entry['qualityLimitationReason']);
      const ri = snap.entries.find(
        (e) => typeName(e) === 'remote-inbound-rtp' && e['ssrc'] === entry['ssrc']
      );
      if (ri) {
        sample.ack = {
          // Any movement in the receiver report counts as "still reporting".
          stamp: JSON.stringify([
            ri['timestamp'],
            ri['roundTripTime'],
            ri['fractionLost'],
            ri['packetsReceived'],
            ri['totalRoundTripTime'],
          ]),
          lossPct: pct(num(ri['fractionLost'])),
          rttMs: scale(num(ri['roundTripTime']), snap.native ? 1 : 1000),
        };
      }
      return sample;
    }
    sample.packetsReceived = num(entry['packetsReceived']);
    sample.packetsLost = num(entry['packetsLost']);
    sample.bytesReceived = num(entry['bytesReceived']);
    sample.frames ??= num(entry['framesDecoded']);
    sample.keyframes ??= num(entry['keyFramesDecoded']);
    sample.pliCount = num(entry['pliCount']);
    sample.freezes ??= num(entry['freezeCount']);
    sample.dropped ??= num(entry['framesDropped']);
    sample.decoder ??= str(entry['decoderImplementation']);
    return sample;
  }

  /**
   * Cumulative bytes of all video RTP streams (payload and headers), for the
   * room-level traffic. Not the candidate pair: with the WebRTC transport the
   * same connection carries the voice data channel, which ODIN's own
   * connection stats already count. Null while no stats source exists.
   */
  async videoTraffic(): Promise<{ at: number; sent: number; received: number } | null> {
    const snap = await this.snapshot();
    if (!snap) return null;
    let sent = 0;
    let received = 0;
    for (const e of snap.entries) {
      if (e['kind'] === 'audio') continue;
      const type = typeName(e);
      if (type === 'outbound-rtp') {
        sent += (num(e['bytesSent']) ?? 0) + (num(e['headerBytesSent']) ?? 0);
      } else if (type === 'inbound-rtp') {
        received += (num(e['bytesReceived']) ?? 0) + (num(e['headerBytesReceived']) ?? 0);
      }
    }
    return { at: snap.at, sent, received };
  }

  /** Bitrate, fps and keyframe rate from the delta to the previous snapshot. */
  private addRates(
    stats: StreamStats,
    snap: RawSnapshot,
    entry: StatsEntry,
    target: StatsTarget
  ): void {
    const prevSnap = this.prevSnapshot;
    const prev = prevSnap && this.findRtpEntry(prevSnap, target);
    if (!prev || !prevSnap) return;
    const dt = (snap.at - prevSnap.at) / 1000;
    if (dt <= 0.2) return;
    const send = target.direction === 'send';
    const id = target.mediaId !== undefined ? String(target.mediaId) : undefined;
    const rate = (now: number | undefined, before: number | undefined) =>
      now !== undefined && before !== undefined && now >= before ? (now - before) / dt : undefined;

    const bytesKey = send ? 'bytesSent' : 'bytesReceived';
    const bytesPerSec = rate(num(entry[bytesKey]), num(prev[bytesKey]));
    if (bytesPerSec !== undefined) stats.bitrateKbps = Math.round((bytesPerSec * 8) / 1000);

    if (stats.fps === undefined) {
      // The native report has no frame counters; the pipeline registry does.
      const framesKey = send ? 'framesEncoded' : 'framesDecoded';
      const fps = rate(
        num(entry[framesKey]) ?? (id ? snap.pipeline[id]?.frames : undefined),
        num(prev[framesKey]) ?? (id ? prevSnap.pipeline[id]?.frames : undefined)
      );
      if (fps !== undefined) stats.fps = Math.round(fps);
    }

    const keyframesKey = send ? 'keyFramesEncoded' : 'keyFramesDecoded';
    const keyframes = rate(
      num(entry[keyframesKey]) ?? (id ? snap.pipeline[id]?.keyframes : undefined),
      num(prev[keyframesKey]) ?? (id ? prevSnap.pipeline[id]?.keyframes : undefined)
    );
    if (keyframes !== undefined) stats.keyframesPerSec = Math.round(keyframes * 10) / 10;
  }

  /**
   * Fetches or reuses the shared snapshot. A smaller `maxAgeMs` lets the
   * health service's startup phase demand a fresher one.
   */
  private async snapshot(maxAgeMs = 900): Promise<RawSnapshot | null> {
    if (this.lastSnapshot && Date.now() - this.lastSnapshot.at < maxAgeMs) {
      return this.lastSnapshot;
    }
    this.snapshotInFlight ??= this.fetchSnapshot().finally(() => {
      this.snapshotInFlight = null;
    });
    return this.snapshotInFlight;
  }

  private async fetchSnapshot(): Promise<RawSnapshot | null> {
    try {
      const snap = isTauri ? await this.fetchNative() : await this.fetchWeb();
      if (snap) {
        this.prevSnapshot = this.lastSnapshot;
        this.lastSnapshot = snap;
      }
      return snap;
    } catch (e) {
      // "video session not open" is routine without video; log at most
      // every 10 s while someone polls.
      if (Date.now() - this.lastSnapshotErrorAt > 10_000) {
        this.lastSnapshotErrorAt = Date.now();
        debug('[stats] snapshot failed', { error: String(e) });
      }
      return null;
    }
  }

  private async fetchNative(): Promise<RawSnapshot | null> {
    const { invoke } = await import('@tauri-apps/api/core');
    // A snapshot that never settles would wedge the in-flight dedupe for
    // the rest of the session, so a slow answer counts as a failed one.
    const [raw, voice] = await Promise.all([
      withTimeout(
        invoke<{
          rtc: Record<string, StatsEntry>;
          pipeline: RawSnapshot['pipeline'];
          media: RawSnapshot['media'];
        }>('plugin:odin|webrtc_video_connection_stats'),
        3_000
      ),
      withTimeout(
        invoke<{ rtt: number } | null>('plugin:odin|odin_voice_room_connection_stats'),
        3_000
      ).catch(() => null),
    ]);
    return {
      at: Date.now(),
      native: true,
      entries: Object.values(raw.rtc ?? {}),
      media: raw.media ?? {},
      pipeline: raw.pipeline ?? {},
      transportRttMs: voice && voice.rtt > 0 ? Math.round(voice.rtt) : undefined,
    };
  }

  private async fetchWeb(): Promise<RawSnapshot | null> {
    if (!this.webStatsSource) return null;
    const report = await withTimeout(this.webStatsSource(), 3_000);
    if (!report) return null;
    const entries: StatsEntry[] = [];
    report.forEach((entry) => entries.push(entry as StatsEntry));
    return { at: Date.now(), native: false, entries, media: {}, pipeline: {} };
  }

  /**
   * The inbound-/outbound-rtp entry of the target stream, matched by its
   * explicit identity only. Without a match the stream stays unknown: falling
   * back to the only entry would hand one stream's counters to another (a
   * camera's to a screen share that never arrives) and report it healthy.
   */
  private findRtpEntry(snap: RawSnapshot, target: StatsTarget): StatsEntry | null {
    const wanted = target.direction === 'send' ? 'outbound-rtp' : 'inbound-rtp';
    const rtp = snap.entries.filter((e) => typeName(e) === wanted && e['kind'] !== 'audio');

    if (target.mediaId !== undefined) {
      const link = snap.media[String(target.mediaId)];
      if (!link) return null;
      return (
        rtp.find(
          (e) =>
            (link.ssrc !== undefined && e['ssrc'] === link.ssrc) ||
            (link.mid !== undefined && e['mid'] === link.mid)
        ) ?? null
      );
    }
    if (target.trackId !== undefined) {
      return (
        rtp.find(
          (e) => e['trackIdentifier'] === target.trackId || e['trackId'] === target.trackId
        ) ??
        // Chrome's outbound-rtp has no trackIdentifier; it links through
        // mediaSourceId to a media-source entry that carries it.
        this.findViaMediaSource(snap, rtp, target.trackId)
      );
    }
    return null;
  }

  private findViaMediaSource(
    snap: RawSnapshot,
    rtp: StatsEntry[],
    trackId: string
  ): StatsEntry | null {
    const source = snap.entries.find(
      (e) => typeName(e) === 'media-source' && e['trackIdentifier'] === trackId
    );
    if (!source) return null;
    return rtp.find((e) => e['mediaSourceId'] === source['id']) ?? null;
  }

  private codecFromEntry(snap: RawSnapshot, entry: StatsEntry): string | undefined {
    const codecId = entry['codecId'];
    if (typeof codecId !== 'string') return undefined;
    const codec = snap.entries.find((e) => e['id'] === codecId);
    const mime = codec && str(codec['mimeType']);
    return mime?.split('/')[1]?.toUpperCase();
  }

  private remoteInboundRtt(snap: RawSnapshot): number | undefined {
    for (const e of snap.entries) {
      if (typeName(e) !== 'remote-inbound-rtp') continue;
      const rtt = num(e['roundTripTime']);
      if (rtt !== undefined && rtt > 0) return scale(rtt, snap.native ? 1 : 1000);
    }
    return undefined;
  }

  private candidatePairRtt(snap: RawSnapshot): number | undefined {
    const pair = snap.entries.find(
      (e) =>
        typeName(e) === 'candidate-pair' && (e['nominated'] === true || e['state'] === 'succeeded')
    );
    const rtt = pair ? num(pair['currentRoundTripTime']) : undefined;
    // The native stack never measures this and reports 0: unknown, not 0 ms.
    if (rtt === undefined || rtt <= 0) return undefined;
    return scale(rtt, snap.native ? 1 : 1000);
  }
}

/** Rejects after `ms`, so a hung stats source reads as a failure. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`stats timeout after ${ms}ms`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    );
  });
}

function typeName(e: StatsEntry): string | undefined {
  return str(e['type']);
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function scale(v: number | undefined, factor: number): number | undefined {
  return v === undefined ? undefined : Math.round(v * factor * 10) / 10;
}

/** RFC 3550 fraction lost, either as 0..255 fixed point or pre-divided 0..1. */
function pct(fractionLost: number | undefined): number | undefined {
  if (fractionLost === undefined) return undefined;
  const f = fractionLost > 1 ? fractionLost / 256 : fractionLost;
  return Math.round(f * 1000) / 10;
}
